import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import { PEV } from "../../../../../../../common/utils";
import {isActiveReview} from "../../../static/reviewHistory/model";
import {loadEditedTasks} from "../editedTasksLocalStorage";
import * as service from "../service";
import {iif} from "../../../../../../../common/utils/functions";
import { UpdateReviewTaskActions } from "./UpdateReviewTasks";

// This is global model for holding all reviews, editedReviewTasks and submittedReviewTasks

export const emptyReview = () => ({
    reviewTasks: PEV({
        columns: [],
        dataSource: [],
    }),
    name: null,
    status: null,
    appId: null,
    reviewId: null,
});

const emptySubmittedReview = () => ({
    reviewTasks: PEV({
        updatedIds: [],
        rejected: [],
        showTaskRejectedReason: true,
    }),
    appId: null,
    reviewId: null,
    showPopup: false,
});

const emptySelectedReviewTaskKeys = () => ({
    keys: [],
    reviewId: null,
    action: null,
    comment: null,
    records: [], // only used for converting from selected to edited records
});

const initialState = {
    reviews: [],
    editedReviewTasks: loadEditedTasks(),
    previewReviewTasks: PEV({
        columns: [],
        dataSource: [],
    }),
    batchSelectedReviewTaskKeys: [],
    submittedReviews: [],
    reasons:[]
};

const getReviewTasks = createAsyncThunk("applications/get-review-tasks", async (payload) => {
    return await service.getReviewTasks(payload);
});

const getPreviewReviewTasks = createAsyncThunk("applications/get-review-tasks/preview", async (payload) => {
    return await service.getReviewTasks(payload);
});

const postReviewTasks = createAsyncThunk("applications/post-review-tasks", async (payload) => {
    try {
        return await service.postReviewTasks(payload);
    } catch (err) {
        throw {
            statusText: err.response?.data.errors[0]?.errorKey,
            message: err.response?.data.errors[0]?.errorMessage, // serializable
        };
    }
});

const findReviewIndex = (state, reviewId) =>
    state.reviews.findIndex((review) => review.reviewId === reviewId);

const findEditedReviewTaskIndex = (state, reviewTaskId) =>
    state.editedReviewTasks.findIndex((reviewTask) => reviewTask.reviewTaskId === reviewTaskId);

const findSubmittedReviewIndex = (state, reviewId) =>
    state.submittedReviews.findIndex((review) => review.reviewId === reviewId);

const findSelectedBatchReviewIndex = (state, reviewId) =>
    state.batchSelectedReviewTaskKeys.findIndex((review) => review.reviewId === reviewId);

const removeSubmittedReviewTasksFromLocalStorage = (action, state) => {
    const submittedReviewTaskIds = action.payload.dataSource.filter(
        (reviewTask) => reviewTask.status === "SUBMITTED").map((reviewTask) => reviewTask.reviewTaskId);
    state.editedReviewTasks = state.editedReviewTasks.filter((reviewTask) =>
        !submittedReviewTaskIds.includes(reviewTask.reviewTaskId));
}

export const ReviewTasksSlice = createSlice({
    name: "review-tasks",
    initialState,
    reducers: {
        createReviewTaskIfNotExits(state, action) {
            const {reviewId} = action.payload;

            let reviewIndex = findReviewIndex(state, reviewId);
            // if review not exists, add new review
            if (reviewIndex === -1) {
                const review = {...emptyReview(), ...action.payload};
                state.reviews.push(review);
                return;
            }
            // if review exists, update at the index
            state.reviews[reviewIndex] = {...state.reviews[reviewIndex], ...action.payload};
        },
        updateReviewStatus(state, action) {
            const reviews = action.payload;
            reviews?.forEach((review) => {
                let reviewTabIndex = findReviewIndex(state, review.reviewId);
                if (reviewTabIndex !== -1) {
                    const status = {status: review.status.status};
                    state.reviews[reviewTabIndex] = {...state.reviews[reviewTabIndex], ...status};
                }
            });
        },
        resetReviewTasks(state) {
            state.reviews = initialState.reviews;
            state.editedReviewTasks = initialState.editedReviewTasks;
            state.submittedReviews = initialState.submittedReviews;
            state.batchSelectedReviewTaskKeys = initialState.batchSelectedReviewTaskKeys;
        },
        setSubmittedReviewTasks(state, action) {
            const { reviewId, appId, value, showPopup } = action.payload;
            const reviewIndex = findSubmittedReviewIndex(state, reviewId);
            let emptyReview = { ...emptySubmittedReview(), reviewId, appId, showPopup };
            if (reviewIndex === -1) {
              state.submittedReviews.push(emptyReview);
            } else {
              state.submittedReviews[reviewIndex] = emptyReview;
            }
            emptyReview = findSubmittedReviewIndex(state, reviewId);
            state.submittedReviews[emptyReview].reviewTasks = PEV(value);
        },
        resetReviewTasksApiResponses(state) {
            state.reviews = initialState.reviews;
        },
        removeReviewTaskIfExists(state, action) {
            const {reviewId} = action.payload;
            let reviewIndex = findReviewIndex(state, reviewId);
            if (reviewIndex !== -1) {
                state.reviews.splice(reviewIndex, 1); // remove review/reviewTasks at given reviewIndex
            }
        },
        removeActiveReview(state, action) {
            const {appId} = action.payload;
            const reviewIndex = state.reviews.findIndex(
                ({status, ...r}) => isActiveReview(status) && r.appId === appId
            );
            if (reviewIndex !== -1) {
                state.reviews.splice(reviewIndex, 1); // remove review/reviewTasks at given reviewIndex
            }
        },
        upsertEditedReviewTask(state, action) {
            const {reviewTaskId} = action.payload;
            let reviewTaskIndex = findEditedReviewTaskIndex(state, reviewTaskId);
            // if review task not exists, add new review task
            if (reviewTaskIndex === -1) {
                state.editedReviewTasks.push(action.payload);
                return;
            }
            // if review task exists, update at the index
            state.editedReviewTasks[reviewTaskIndex] = {
                ...state.editedReviewTasks[reviewTaskIndex],
                ...action.payload,
            };
        },
        resetComment(state, action) {
            const {reviewTaskId} = action.payload;
            let reviewTaskIndex = findEditedReviewTaskIndex(state, reviewTaskId);
            if (reviewTaskIndex !== -1) {
                state.editedReviewTasks[reviewTaskIndex].comment = null
            }
        },
        removeEditedReviewTasks(state, action) {
            const {reviewId} = action.payload;
            state.editedReviewTasks = state.editedReviewTasks.filter(
                (reviewTask) => reviewTask.reviewId !== reviewId
            );
        },
        removeEditedReviewTasksByTaskIdsIfExists(state, action) {
            const {reviewTaskIds} = action.payload;
            state.editedReviewTasks = state.editedReviewTasks.filter(
                (reviewTask) => !reviewTaskIds.some((reviewTaskId) => reviewTask.reviewTaskId === reviewTaskId)
            );
        },
        upsertBatchSelectedReviewTasksKeys(state, action) {
            const {reviewId} = action.payload;
            let reviewTaskIndex = findSelectedBatchReviewIndex(state, reviewId);
            // if review task not exists, add new review task
            if (reviewTaskIndex === -1) {
                const review = {...emptySelectedReviewTaskKeys(), ...action.payload};
                state.batchSelectedReviewTaskKeys.push(review);
                return;
            }
            // if review task exists, update at the index
            state.batchSelectedReviewTaskKeys[reviewTaskIndex] = {
                ...state.batchSelectedReviewTaskKeys[reviewTaskIndex],
                ...action.payload,
            };
        },
        removeBatchSelectedReviewTasks(state, action) {
            const {reviewId} = action.payload;
            state.batchSelectedReviewTaskKeys = state.batchSelectedReviewTaskKeys.filter(
                (review) => review.reviewId !== reviewId
            );
        },
        convertBatchSelectedToBatchEdited(state, action) {
            const {reviewId} = action.payload;
            const {
                records,
                action: reviewAction,
                comment,
                decisionReason,
                reviewedBy,
                reviewedAt,
            } = findBatchSelectedReviewTaskKeysByReviewId(state, reviewId);
            for (let record of records) {
                const {reviewTaskId} = record;
                const editedRecord = {
                    ...record,
                    action: reviewAction,
                    decisionReason,
                    comment,
                    reviewedBy,
                    reviewedAt,
                };
                let reviewTaskIndex = findEditedReviewTaskIndex(state, reviewTaskId);
                // if review task not exists, add new review task
                if (reviewTaskIndex === -1) {
                    state.editedReviewTasks.push(editedRecord);
                }
                // if review task exists, update at the index
                state.editedReviewTasks[reviewTaskIndex] = editedRecord;
            }
        },
        removeSubmittedReviewTaskIfExists(state, action) {
            const {reviewId} = action.payload;
            let reviewIndex = findSubmittedReviewIndex(state, reviewId);
            if (reviewIndex !== -1) {
                state.submittedReviews.splice(reviewIndex, 1); // remove review/reviewTasks at given reviewIndex
            }
        },
        disableUpdatedReviewTaskPopup(state, action) {
            const {reviewId} = action.payload;
            let reviewIndex = findSubmittedReviewIndex(state, reviewId);
            if (reviewIndex !== -1) {
                state.submittedReviews[reviewIndex].showPopup = false;
            }
        },
        syncLatestReviewTasks(state, action) {
            const previewTasks = state.previewReviewTasks.value;
            if (previewTasks.dataSource.length > 0) {
                const {reviewId} = action.payload;
                let reviewIndex = findReviewIndex(state, reviewId);
                if (state.reviews[reviewIndex]) {
                    state.reviews[reviewIndex].reviewTasks = PEV(previewTasks);
                }
                iif(function removeSubmittedTasksFromLocalStorage() {
                    const submittedReviewTaskIds = previewTasks.dataSource.filter(
                        (reviewTask) => reviewTask.status === "SUBMITTED").map((reviewTask) => reviewTask.reviewTaskId);
                    state.editedReviewTasks = state.editedReviewTasks.filter((reviewTask) =>
                        !submittedReviewTaskIds.includes(reviewTask.reviewTaskId));
                })
            }
        }
    },
    extraReducers: {
        // Add reducers for setting draft review tasks and fetching draft review tasks
        [getPreviewReviewTasks.pending.type]: (state) => {
            state.previewReviewTasks.progress = "Fetching draft review-tasks"
        },
        [getPreviewReviewTasks.fulfilled.type]: (state, action) => {
            state.previewReviewTasks.value = action.payload;
            state.previewReviewTasks.progress = null;
        },
        [getPreviewReviewTasks.rejected.type]: (state, action) => {
            state.previewReviewTasks.error = action.error;
            state.previewReviewTasks.progress = null;
        },
        [getReviewTasks.pending.type]: (state, pendingAction) => {
            const {reviewId} = pendingAction.meta.arg;
            let reviewIndex = findReviewIndex(state, reviewId);
            state.reviews[reviewIndex].reviewTasks.value.dataSource = []; // explicitly empty the datasource, since table is not rendering on data change
            state.reasons = []; // explicitly empty the datasource, since table is not rendering on data change
            state.reviews[reviewIndex].reviewTasks.progress = "Fetching review-tasks";
        },
        [getReviewTasks.fulfilled.type]: (state, action) => {
            const {reviewId} = action.meta.arg;
            let reviewIndex = findReviewIndex(state, reviewId);
            if (state.reviews[reviewIndex]) {
                // extra check to make sure, review is not removed due to tab close
                state.reviews[reviewIndex].reviewTasks = PEV({dataSource:action.payload.dataSource,columns:action.payload.columns});
            }
            removeSubmittedReviewTasksFromLocalStorage(action, state);
            state.reasons=action.payload.decisionReasons
        },
        [getReviewTasks.rejected.type]: (state, rejectedAction) => {
            const {reviewId} = rejectedAction.meta.arg;
            let reviewIndex = findReviewIndex(state, reviewId);
            if (state.reviews[reviewIndex]) {
                // // extra check to make sure, review is not removed due to tab close
                state.reviews[reviewIndex].reviewTasks.progress = null;
                state.reviews[reviewIndex].reviewTasks.error = rejectedAction.error;
            }
            state.reasons=[]
        },
        [postReviewTasks.pending.type]: (state, pendingAction) => {
            const {reviewId, appId} = pendingAction.meta.arg;
            const reviewIndex = findSubmittedReviewIndex(state, reviewId);
            const emptyReview = {...emptySubmittedReview(), reviewId, appId};
            emptyReview.reviewTasks.progress = `Submitting review tasks for ${appId} - ${reviewId}`;
            if (reviewIndex === -1) {
                state.submittedReviews.push(emptyReview);
            } else {
                state.submittedReviews[reviewIndex] = emptyReview;
            }
        },
        [postReviewTasks.fulfilled.type]: (state, action) => {
            const {reviewId, payload} = action.meta.arg;
            const emptyReviewIndex = findSubmittedReviewIndex(state, reviewId);
            state.submittedReviews[emptyReviewIndex].reviewTasks = PEV({
                ...action.payload,
                showTaskRejectedReason: payload.action !== UpdateReviewTaskActions.BatchTasksReassignmentRequest
            });
            state.submittedReviews[emptyReviewIndex].showPopup = true;
        },
        [postReviewTasks.rejected.type]: (state, rejectedAction) => {
            const {reviewId} = rejectedAction.meta.arg;
            const emptyReviewIndex = findSubmittedReviewIndex(state, reviewId);
            state.submittedReviews[emptyReviewIndex].reviewTasks.progress = null;
            state.submittedReviews[emptyReviewIndex].reviewTasks.error = rejectedAction.error;
            state.submittedReviews[emptyReviewIndex].showPopup = true;
        },
    },
});

export const findReviewById = (state, reviewId) =>
    state.reviews.find((review) => review.reviewId === reviewId);
export const findEditedReviewTasksByReviewId = (state, reviewId) =>
    state.editedReviewTasks.filter((review) => review.reviewId === reviewId);
export const findBatchSelectedReviewTaskKeysByReviewId = (state, reviewId) =>
    state.batchSelectedReviewTaskKeys.find((review) => review.reviewId === reviewId);
export const findSubmittedReviewByReviewId = (state, reviewId) =>
    state.submittedReviews.find((review) => review.reviewId === reviewId);

// export static constant values from store
export const getCurrentModelState = () => window.store.getState().appReviewTasks;
export const getEditedReviewTasks = (reviewId) =>
    findEditedReviewTasksByReviewId(getCurrentModelState(), reviewId);
export const hasEditedReviewTasks = (reviewId) => getEditedReviewTasks(reviewId).length > 0;
export const isMyContent = (reviewId) =>
  findReviewById(getCurrentModelState(), reviewId)?.reviewTasks?.value?.dataSource?.some(
    (d) => d.isAssignedToCurrentUser
  );
export const isNotAllMyContent = (reviewId) =>
  findReviewById(getCurrentModelState(), reviewId)?.reviewTasks?.value?.dataSource?.some(
    (d) => !d.isAssignedToCurrentUser
  );

export const AppReviewTasksReducer = ReviewTasksSlice.reducer;
const {
  createReviewTaskIfNotExits,
  disableUpdatedReviewTaskPopup,
  removeReviewTaskIfExists,
  upsertEditedReviewTask,
  removeEditedReviewTasks,
  removeSubmittedReviewTaskIfExists,
  upsertBatchSelectedReviewTasksKeys,
  removeBatchSelectedReviewTasks,
  convertBatchSelectedToBatchEdited,
  removeActiveReview,
  updateReviewStatus,
  resetReviewTasks,
  resetReviewTasksApiResponses,
  resetComment,
  removeEditedReviewTasksByTaskIdsIfExists,
  syncLatestReviewTasks,
  setSubmittedReviewTasks,
} = ReviewTasksSlice.actions;

export const AppReviewTasksActions = {
  getReviewTasks,
  getPreviewReviewTasks,
  postReviewTasks,
  createReviewTaskIfNotExits,
  removeReviewTaskIfExists,
  upsertEditedReviewTask,
  syncLatestReviewTasks,
  removeEditedReviewTasks,
  removeSubmittedReviewTaskIfExists,
  disableUpdatedReviewTaskPopup,
  upsertBatchSelectedReviewTasksKeys,
  removeBatchSelectedReviewTasks,
  convertBatchSelectedToBatchEdited,
  removeActiveReview,
  updateReviewStatus,
  resetReviewTasksApiResponses,
  resetReviewTasks,
  resetComment,
  removeEditedReviewTasksByTaskIdsIfExists,
  setSubmittedReviewTasks,
};
